import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import DataTable from "../../common/DataTable";
import PageLoader from "../../ui/PageLoader";
import {
  displayTPRMValue,
  getTPRMErrorMessage,
  normalizeTPRMPage,
} from "./tprmPageUtils";
import useTPRMDialog from "./useTPRMDialog";

const PAGE_SIZE = 20;

const StatusBadge = ({ value }) => {
  const status = displayTPRMValue(value);
  const color =
    {
      Pending: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
      "Pending Review": "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
      "In Progress": "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200",
      "Pending Verification": "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200",
      Approved: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
      Verified: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
      Resolved: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
      Closed: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
      Rejected: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200",
      Expired: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200",
      Revoked: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
      Active: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
      Draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
      Terminated: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
      "Conditionally Approved": "bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-200",
      Deferred: "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200",
      "Changes Requested": "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200",
    }[status] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${color}`}>
      {status}
    </span>
  );
};

export { StatusBadge };

const ResourceActionDialog = ({
  selection,
  onClose,
  onSubmit,
  loading,
  error,
  returnFocusRef,
}) => {
  const { item, values } = selection;
  const fields = item.dialog?.fields || [];
  const [formValues, setFormValues] = useState(values || {});
  const dialogRef = useTPRMDialog({
    onClose,
    closeOnEscape: !loading,
    returnFocusRef,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/60 p-4">
      <section
        aria-labelledby="tprm-action-title"
        aria-modal="true"
        className="my-auto w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900 sm:p-6"
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white" id="tprm-action-title">
          {item.dialog?.title || item.label}
        </h2>
        {item.dialog?.description && (
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            {item.dialog.description}
          </p>
        )}
        <form
          className="mt-4 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit(formValues);
          }}
        >
          {fields.map((field) => (
            <label
              className="block text-sm font-medium text-slate-700 dark:text-slate-300"
              key={field.name}
            >
              {field.label}
              {field.type === "select" ? (
                <select
                  aria-label={field.label}
                  className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  onChange={(event) =>
                    setFormValues((current) => ({
                      ...current,
                      [field.name]: event.target.value,
                    }))
                  }
                  required={field.required}
                  value={formValues[field.name] ?? field.defaultValue ?? ""}
                >
                  <option value="">Select...</option>
                  {field.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.type === "textarea" ? (
                <textarea
                  aria-label={field.label}
                  className="mt-1 min-h-24 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  onChange={(event) =>
                    setFormValues((current) => ({
                      ...current,
                      [field.name]: event.target.value,
                    }))
                  }
                  required={field.required}
                  value={formValues[field.name] ?? ""}
                />
              ) : (
                <input
                  aria-label={field.label}
                  className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  onChange={(event) =>
                    setFormValues((current) => ({
                      ...current,
                      [field.name]: event.target.value,
                    }))
                  }
                  required={field.required}
                  type={field.type || "text"}
                  value={formValues[field.name] ?? ""}
                />
              )}
            </label>
          ))}
          {error && (
            <p className="text-sm text-rose-700 dark:text-rose-300" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-200"
              disabled={loading}
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
              disabled={loading}
              type="submit"
            >
              {loading ? "Working..." : item.label}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

const TPRMResourceTable = ({
  title,
  description,
  fetchPage,
  columns,
  filters = [],
  getRowId,
  getActions,
  performAction,
  toolbarActions,
  successLabel,
  emptyMessage = "No records found.",
  refreshKey: externalRefreshKey = 0,
}) => {
  const {
    user,
    isPlatformAdmin,
    isCustomerAdmin,
    isSecurityAnalyst,
  } = useAuth();
  const permissions = {
    canWrite: Boolean(isPlatformAdmin || isCustomerAdmin || isSecurityAnalyst),
    canApprove: Boolean(isPlatformAdmin || isCustomerAdmin),
    actorId: user?.id ? String(user.id) : "",
  };
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [columnFilters, setColumnFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [selection, setSelection] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const actionTriggerRef = useRef(null);

  const filterValues = useMemo(
    () =>
      Object.fromEntries(
        columnFilters.map(({ id, value }) => [id, value]),
      ),
    [columnFilters],
  );

  useEffect(() => {
    let cancelled = false;
    const loadPage = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchPage({
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          ...filterValues,
        });
        const page = normalizeTPRMPage(data);
        if (!cancelled) {
          setRows(page.results);
          setRowCount(page.count);
          setHasLoaded(true);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(getTPRMErrorMessage(requestError, `Unable to load ${title.toLowerCase()}.`));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadPage();
    return () => {
      cancelled = true;
    };
  }, [
    externalRefreshKey,
    fetchPage,
    filterValues,
    pagination.pageIndex,
    pagination.pageSize,
    reloadKey,
    title,
  ]);

  const tableColumns = useMemo(() => {
    const baseColumns = columns.map((column) => {
      const filter = filters.find((item) => item.id === column.id || item.id === column.accessorKey);
      return filter
        ? { ...column, meta: { ...column.meta, filter: filter.definition } }
        : column;
    });
    if (!getActions) return baseColumns;
    return [
      ...baseColumns,
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => {
          const availableActions = getActions(row.original, permissions);
          if (!availableActions.length) return <span className="text-slate-400">—</span>;
          return (
            <div className="flex flex-wrap gap-2">
              {availableActions.map((item) => (
                <button
                  aria-label={`${item.label} for ${row.id}`}
                  className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                    item.variant === "danger"
                      ? "border-rose-200 text-rose-700 hover:bg-rose-50 dark:border-rose-900 dark:text-rose-300"
                      : "border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-900 dark:text-blue-300 dark:hover:bg-slate-800"
                  }`}
                  disabled={actionLoading || item.disabled}
                  key={`${item.action}-${item.payload?.condition_index ?? ""}`}
                  onClick={(event) => {
                    setActionError("");
                    if (item.dialog) {
                      actionTriggerRef.current = event.currentTarget;
                      setSelection({ row: row.original, item, values: item.initialValues || {} });
                    } else {
                      executeAction(row.original, item);
                    }
                  }}
                  type="button"
                >
                  {item.label}
                </button>
              ))}
            </div>
          );
        },
        size: 210,
      },
    ];
  }, [actionLoading, columns, filters, getActions, permissions]);

  const executeAction = async (row, item, values = {}) => {
    setActionLoading(true);
    setActionError("");
    setFeedback(null);
    try {
      await performAction(row, item, values);
      setSelection(null);
      setFeedback({
        type: "success",
        message: successLabel || `${item.label} completed successfully.`,
      });
      setReloadKey((current) => current + 1);
    } catch (actionRequestError) {
      setActionError(getTPRMErrorMessage(actionRequestError, `${item.label} failed.`));
      if (!item.dialog) {
        setFeedback({
          type: "error",
          message: getTPRMErrorMessage(actionRequestError, `${item.label} failed.`),
        });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleColumnFiltersChange = (updater) => {
    const next = typeof updater === "function" ? updater(columnFilters) : updater;
    setColumnFilters(next);
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  };

  if (loading && !hasLoaded) {
    return <PageLoader title={`Loading ${title.toLowerCase()}...`} minH="min-h-[40vh]" />;
  }

  if (error) {
    return (
      <section
        className="rounded-xl border border-rose-200 bg-white p-6 dark:border-rose-900 dark:bg-slate-900"
        role="alert"
      >
        <h2 className="font-semibold text-slate-900 dark:text-white">
          {title} could not be loaded
        </h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{error}</p>
        <button
          className="mt-4 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          onClick={() => setReloadKey((current) => current + 1)}
          type="button"
        >
          Try again
        </button>
      </section>
    );
  }

  return (
    <>
      <section aria-labelledby="tprm-resource-heading" className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2
              className="text-lg font-semibold text-slate-900 dark:text-white"
              id="tprm-resource-heading"
            >
              {title}
            </h2>
            {description && (
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                {description}
              </p>
            )}
          </div>
          {toolbarActions}
        </div>
        {feedback && (
          <p
            className={`rounded-lg border p-3 text-sm ${
              feedback.type === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200"
                : "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200"
            }`}
            role={feedback.type === "success" ? "status" : "alert"}
          >
            {feedback.message}
          </p>
        )}
        <DataTable
          columns={tableColumns}
          data={rows}
          loading={loading}
          error={null}
          emptyMessage={emptyMessage}
          enableSorting={false}
          enableGlobalFilter={false}
          enableColumnFilters={filters.length > 0}
          enableColumnVisibility={false}
          manualPagination
          manualFiltering
          rowCount={rowCount}
          pageCount={Math.ceil(rowCount / pagination.pageSize)}
          pagination={pagination}
          onPaginationChange={(updater) =>
            setPagination((current) =>
              typeof updater === "function" ? updater(current) : updater,
            )
          }
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          getRowId={getRowId}
          className="min-w-0 [&_.overflow-x-auto]:dark:border-slate-700 [&_.overflow-x-auto]:dark:bg-slate-900 [&_thead]:dark:bg-slate-800 [&_thead]:dark:text-slate-300 [&_th]:dark:border-slate-700 [&_tbody]:dark:divide-slate-800 [&_tbody_tr]:dark:hover:bg-slate-800 [&_tbody_td]:dark:text-slate-300 [&_td]:dark:border-slate-800 [&_tfoot]:dark:bg-slate-900 [&_tfoot]:dark:text-slate-300"
        />
      </section>
      {selection && (
        <ResourceActionDialog
          error={actionError}
          loading={actionLoading}
          onClose={() => {
            if (!actionLoading) setSelection(null);
          }}
          onSubmit={(values) => executeAction(selection.row, selection.item, values)}
          returnFocusRef={actionTriggerRef}
          selection={selection}
        />
      )}
    </>
  );
};

export default TPRMResourceTable;
